import { useCallback } from "react";
import { rollbar } from "@libs/utils/rollbar";
import { useRequestSmsVerificationMutation } from "./__generated__/mutations";

export type SmsVerificationGateResult =
  | { allowed: true }
  | { allowed: false; reason: "limitExceeded" | "invalidPhoneNumber" };

type RequestSmsVerification = {
  requestSmsVerification: (
    phoneNumber: string
  ) => Promise<SmsVerificationGateResult>;
};

/**
 * Calls the backend SMS rate-limit gate before a verification code is sent.
 *
 * The gate (phone/IP threshold) is the actual control that prevents the
 * project-wide `auth/quota-exceeded`; callers must await it and skip
 * `signInWithPhoneNumber()` when `allowed` is false.
 *
 * The gate is advisory (it can be bypassed without App Check enforcement), so
 * any transport/server error fails OPEN: blocking every login on a gate outage
 * would be worse than letting it through, and the client cooldown plus
 * Firebase's own limits still apply.
 */
export const useRequestSmsVerification = (): RequestSmsVerification => {
  const [mutate] = useRequestSmsVerificationMutation();

  const requestSmsVerification = useCallback(
    async (phoneNumber: string): Promise<SmsVerificationGateResult> => {
      try {
        const result = await mutate({ variables: { input: { phoneNumber } } });
        const data = result.data?.requestSmsVerification;

        switch (data?.__typename) {
          case "UsersPayloadsRequestSmsVerificationPayload":
            return data.allowed
              ? { allowed: true }
              : { allowed: false, reason: "limitExceeded" };
          case "RequestSmsVerificationResult_LimitExceededError":
            return { allowed: false, reason: "limitExceeded" };
          case "RequestSmsVerificationResult_InvalidPhoneNumberError":
            return { allowed: false, reason: "invalidPhoneNumber" };
          default:
            // Unexpected/empty response: fail open (see hook doc comment).
            rollbar?.error(
              new Error("requestSmsVerification returned an unexpected result")
            );
            return { allowed: true };
        }
      } catch (err: unknown) {
        rollbar?.error(err instanceof Error ? err : new Error(String(err)));
        return { allowed: true };
      }
    },
    [mutate]
  );

  return { requestSmsVerification };
};
